"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import type { GratitudeEntry } from "@/lib/db/schema"
import { Loader2, Heart } from "lucide-react"

interface DeleteGratitudeDialogProps {
  entry: GratitudeEntry
  open: boolean
  onOpenChange: (open: boolean) => void
  onDelete: (entryId: string) => Promise<void>
}

export function DeleteGratitudeDialog({
  entry,
  open,
  onOpenChange,
  onDelete,
}: DeleteGratitudeDialogProps) {
  const [deleting, setDeleting] = useState(false)

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await onDelete(entry.id)
      onOpenChange(false)
    } catch (error) {
      console.error("Error deleting gratitude entry:", error)
    } finally {
      setDeleting(false)
    }
  }

  const formattedDate = new Date(entry.date).toLocaleDateString("cs-CZ", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Smazat záznam vděčnosti?</AlertDialogTitle>
          <AlertDialogDescription>
            Opravdu chceš smazat záznam vděčnosti ze dne <strong>{formattedDate}</strong>?
            <span className="flex items-center gap-2 mt-2 text-pink-600">
              <Heart className="h-4 w-4" />
              Vděčnost
            </span>
            {entry.content && (
              <span className="block mt-2 text-xs italic whitespace-pre-wrap">
                "{entry.content.slice(0, 150)}
                {entry.content.length > 150 ? "..." : ""}"
              </span>
            )}
            <span className="block mt-4 text-destructive">
              Tuto akci nelze vrátit zpět.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Zrušit</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Mažu...
              </>
            ) : (
              "Smazat"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
